import Link from "next/link";
import { generateSingleVideoSlug } from "../../utils/contentful/generateSlugs";
import { ContentfulImage } from "../Contentful/Image";
import Play from "../../assets/play.svg";
import cx from "classnames";
import { VideoGrid } from "./VideoGrid";
import { Carousel } from "../Carousel/Carousel";
import { truncateText } from "../../utils/truncateText";
import { videoRunTime } from "../../utils/dateTime";

type VideoProps = {
  title: string;
  subtitle?: string;
  videos: any[];
  showDescription: boolean;
  showBorderTop: boolean;
  sizes?: { max: number; min: number };
  maxVideos: { desktop: number; mobile: number };
  paginate?: boolean;
  videoClasses?: string;
};

export const VideoScroller: React.FC<{
  videos: any[];
}> = ({ videos }) => {
  return (
    <div className="overflow-y-scroll h-[100%] absolute top-0 right-0 bottom-0 p-[15px] pb-0 bg-coinage-offGray rounded-xl border border-coinage-stroke hide-scrollbar">
      <h4 className="display4 text-coinage-black bold mb-2 mt-[0]">Episodes</h4>
      {videos.map(video => (
        <Video key={video.urlSlug} showDescription={false} video={video} />
      ))}
    </div>
  );
};

export const Video: React.FC<{
  showDescription: boolean;
  showRuntime?: boolean;
  video: any;
  linkClassName?: string;
  className?: string;
  useArticleImage?: boolean;
}> = ({
  showDescription,
  video,
  linkClassName,
  className,
  showRuntime,
  useArticleImage
}) => {
  const hasVideo = video?.video?.videoUpload;
  const image = () => {
    if (useArticleImage) return video.image;
    if (hasVideo) return video.video.fallbackImage;
    return video.image;
  };
  return (
    <Link
      className={linkClassName}
      href={generateSingleVideoSlug(video.seasonGroup.slug, video.urlSlug)}>
      <div
        className={`rounded-[12px] overflow-hidden relative mb-[10px] ${className}`}>
        <ContentfulImage image={image()} />
        {hasVideo && (
          <div className="w-[32px] h-[32px] rounded-[50%] bg-coinage-white absolute bottom-2 left-2 flex justify-center">
            <Play width="10" />
          </div>
        )}
      </div>

      <span className="body2 text-coinage-body ">
        {video?.category?.categoryTitle}
      </span>

      <h4 className="subhead2 mb-2 mt-1">{video.articleTitle}</h4>
      {showDescription && (
        <p className="text-coinage-body">
          {truncateText(
            video.shortArticleDescription ?? video.articleDescription
          )}
        </p>
      )}
      {hasVideo && showRuntime && (
        <p className="text-coinage-body mt-[auto]">
          <span className="bg-coinage-offWhite inline-flex p-[5px] rounded body2 ">
            {videoRunTime(video.video.videoUpload.duration)}
          </span>
        </p>
      )}
    </Link>
  );
};
export const VideoGroup: React.FC<VideoProps> = ({
  title,
  subtitle,
  videos,
  showDescription,
  showBorderTop,
  videoClasses,
  sizes = { max: 360, min: 280 },
  maxVideos = { desktop: 3, mobile: 2 },
  paginate = false
}) => {
  const containerClasses = cx(
    "content-wrapper w-full mx-auto py-[0] tab:py-12",
    {
      "border-t-[1px] border-coinage-stroke": showBorderTop
    }
  );

  const showPageTitle = title || subtitle;

  return (
    <section className={containerClasses}>
      {showPageTitle && (
        <div className="mb-8 px-4 tab:px-0">
          {title && <h2 className="display2 my-2">{title}</h2>}
          {subtitle && <p className="body1 text-coinage-body">{subtitle}</p>}
        </div>
      )}

      <VideoSlider
        videoClasses={videoClasses}
        videos={videos}
        containerClasses="tab:hidden"
      />

      <VideoGrid
        containerClassName="hidden tab:block"
        maxVideos={maxVideos}
        paginate={paginate}
        videos={videos}
        sizes={sizes}
        showDescription={showDescription}
      />
    </section>
  );
};

export const VideoSlider: React.FC<{
  videos: any[];
  videoClasses?: string;
  containerClasses?: string;
}> = ({ videos, videoClasses, containerClasses }) => {
  const sliderVideos = videos.map(video => (
    <Video
      className={videoClasses}
      key={video.urlSlug}
      showDescription
      video={video}
    />
  ));

  return (
    <div className={`px-4 overflow-hidden ${containerClasses}`}>
      <div className="flex">
        <div className="p-0 videoGroupSlider w-full">
          <Carousel className="bg-coinage-light" slides={sliderVideos} />
        </div>
      </div>
    </div>
  );
};

type VideoSliderProps = {
  title: string;
  videos: any[];
  showDescription: boolean;
  showBorderTop: boolean;
};

export const VideoSliderGroup: React.FC<VideoSliderProps> = ({
  title,
  videos,
  showDescription,
  showBorderTop
}) => {
  const containerClasses = cx(
    "max-w-[1200px] w-full mx-auto py-[0] tab:py-12",
    {
      "border-t-[1px] border-coinage-stroke": showBorderTop
    }
  );
  const sliderVideos = videos.map(video => (
    <Link
      className="flex flex-col gap-2 mb-[15px]"
      key={video.urlSlug}
      href={generateSingleVideoSlug(video.seasonGroup.slug, video.urlSlug)}>
      <Video useArticleImage showDescription={showDescription} video={video} />
    </Link>
  ));

  return (
    <div className="px-4 overflow-hidden">
      <section className={containerClasses}>
        <h4 className="subhead3 mb-[15px] mt-4">{title}</h4>
        <div className="flex">
          <div className="p-0 videoGroupSlider w-full">
            <Carousel className="bg-coinage-light" slides={sliderVideos} />
          </div>
        </div>
      </section>
    </div>
  );
};
